import {
  Image,
  TouchableOpacity,
  Text,
  View,
  ImageSourcePropType,
  Pressable,
} from "react-native";
import { useState, useEffect, useMemo, useCallback } from "react";
import {
  LogIn,
  MoreVertical,
  Store,
  Unlink,
  ExternalLink,
} from "lucide-react-native";
import React from "react";
import { useLocalSearchParams } from "expo-router";
import { useAuth } from "@clerk/clerk-react";
import StyledText from "../../components/common/ui/styled-text";
import LoadingView from "../../components/common/loading-view";
import { Badge } from "../../components/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/common/shadcn/dropdown";
import { useAgents } from "../../hooks/useAgents";
import { IAgent } from "../../types/agent";
import { ConnectionResponse } from "../../types/api/connections";
import { useConnectionsService } from "../../services/api/connections-service";
import SpotOnLocationsModal from "../../components/feature/integrations/spoton-locations-modal";
import IntegrationDisconnectConfirmModal from "../../components/feature/integrations/integration-disconnect-confirm-modal";

interface ThirdPartyProvider {
  id: string;
  name: string;
  logo: ImageSourcePropType;
  status: "available" | "connected";
}

const getThirdPartyProviders = (
  isSpotOnConnected: boolean,
  isStreamConnected: boolean,
  agents: IAgent[],
  isAdmin: boolean
): ThirdPartyProvider[] => {
  const providers: ThirdPartyProvider[] = [];

  const hasSpotOnAgents = agents.some(
    (agent) => agent.posProvider === "spoton"
  );
  if (hasSpotOnAgents && isAdmin) {
    providers.push({
      id: "spoton",
      name: "SpotOn",
      logo: require("../../assets/spoton.webp"),
      status: isSpotOnConnected ? "connected" : "available",
    });
  }

  const hasStreamAgents = agents.some(
    (agent) => agent.posProvider === "stream"
  );
  if (hasStreamAgents) {
    providers.push({
      id: "stream",
      name: "Stream",
      logo: require("../../assets/stream.png"),
      status: isStreamConnected ? "connected" : "available",
    });
  }

  return providers;
};

const renderProvidersList = (
  availableProviders: ThirdPartyProvider[],
  getActionButton: (provider: ThirdPartyProvider) => React.ReactNode,
  handleDisconnect: (providerId: string) => void
) => {
  if (availableProviders.length === 0) {
    return (
      <View className="bg-white p-6 border border-gray-200 rounded-xl shadow-sm">
        <View className="text-center">
          <StyledText className="text-gray-900 text-lg font-semibold mb-2">
            None Available
          </StyledText>
          <StyledText className="text-gray-600 text-sm leading-6">
            Contact the team to create an agent before connecting your POS
            system.
          </StyledText>
        </View>
      </View>
    );
  }
  return availableProviders.map((provider) => (
    <View
      key={provider.id}
      className="bg-white px-4 pl-4 sm:py-6 sm:pl-6 sm:pr-4 pr-4 max-sm:pb-4 border border-gray-200 rounded-xl shadow-sm"
    >
      <View className="flex-col sm:flex-row sm:items-center sm:justify-between gap-0 sm:gap-4">
        <View className="flex-row items-center justify-between gap-4 flex-1">
          <View className="flex-row items-center gap-4">
            <Image
              source={provider.logo}
              style={{ width: 130, height: 70 }}
              resizeMode="contain"
            />
            {provider.status === "connected" && (
              <Badge variant="secondary" className="bg-green-100">
                <StyledText className="text-green-800 text-xs">
                  Connected
                </StyledText>
              </Badge>
            )}
          </View>
          {provider.status === "connected" && provider.id === "spoton" && (
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className="h-11 w-11 sm:h-8 sm:w-8 flex items-center justify-center visible max-sm:visible sm:hidden"
              >
                <Pressable className="h-11 w-11 sm:h-8 sm:w-8 flex items-center justify-center flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Pressable>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => handleDisconnect(provider.id)}
                >
                  <Unlink className="w-4 h-4 mr-0.5 text-destructive" />
                  Disconnect
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </View>
        <View className="w-full sm:w-auto">{getActionButton(provider)}</View>
      </View>
    </View>
  ));
};

const Integrations = () => {
  const [connecting, setConnecting] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSpotOnModal, setShowSpotOnModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [providerToDisconnect, setProviderToDisconnect] = useState<
    string | null
  >(null);
  const { data: agents, isLoading: isAgentsLoading } = useAgents();
  const [isOAuthFlow, setIsOAuthFlow] = useState(false);
  const { orgRole } = useAuth();
  const isAdmin = orgRole === "org:admin";

  const {
    startSpotOnOAuth,
    fetchSpotOnConnections,
    deleteSpotOnConnection,
    startStreamOAuth,
  } = useConnectionsService();
  const params = useLocalSearchParams();

  useEffect(() => {
    loadConnections();
  }, []);

  useEffect(() => {
    if (params.openModal === "spoton") {
      setShowSpotOnModal(true);
    }
  }, [params.openModal]);

  const loadConnections = async () => {
    try {
      setLoading(true);
      const data = await fetchSpotOnConnections();
      setConnections(data);
    } catch (error) {
      console.error("Failed to load connections:", error);
    } finally {
      setLoading(false);
    }
  };

  const isSpotOnConnected = useMemo(
    () => connections.some((conn) => conn.provider === "spoton"),
    [connections]
  );

  const isStreamConnected = true;

  const availableProviders = useMemo(
    () =>
      getThirdPartyProviders(
        isSpotOnConnected,
        isStreamConnected,
        agents || [],
        isAdmin
      ),
    [isSpotOnConnected, isStreamConnected, agents, isAdmin]
  );

  const handleConnect = async (providerId: string) => {
    setConnecting(providerId);
    try {
      if (providerId === "spoton") {
        const url = await startSpotOnOAuth();
        window.location.href = url;
      } else if (providerId === "stream") {
        const url = await startStreamOAuth();
        window.open(url, "_blank");
      }
    } catch (error) {
      console.error("Failed to start OAuth:", error);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = (providerId: string) => {
    setProviderToDisconnect(providerId);
    setShowDisconnectModal(true);
  };

  const confirmDisconnect = async () => {
    if (!providerToDisconnect) return;

    setDisconnecting(providerToDisconnect);
    setShowDisconnectModal(false);

    try {
      if (providerToDisconnect === "spoton") {
        await deleteSpotOnConnection();
        await loadConnections();
      }
    } catch (error) {
      console.error("Failed to disconnect:", error);

      // Handle 403 error by starting OAuth flow
      if (error instanceof Error && error.message.includes("403")) {
        setIsOAuthFlow(true);
        try {
          console.log(
            "403 error detected during disconnect, starting SpotOn OAuth flow..."
          );
          const redirectUrl = await startSpotOnOAuth();
          window.location.href = redirectUrl;
        } catch (oauthError) {
          console.error("Failed to start OAuth flow:", oauthError);
          window.alert("Failed to re-authenticate. Please try again.");
        } finally {
          setIsOAuthFlow(false);
        }
      } else {
        window.alert("Failed to disconnect the integration. Please try again.");
      }
    } finally {
      setDisconnecting(null);
      setProviderToDisconnect(null);
    }
  };

  const cancelDisconnect = () => {
    setShowDisconnectModal(false);
    setProviderToDisconnect(null);
  };

  const handleManageLocations = async (provider: ThirdPartyProvider) => {
    if (provider.id === "spoton") {
      setShowSpotOnModal(true);
    } else if (provider.id === "stream") {
      try {
        setConnecting("stream");
        const url = await startStreamOAuth();
        window.open(url, "_blank");
      } catch (error) {
        console.error("Failed to open Stream:", error);
      } finally {
        setConnecting(null);
      }
    }
  };

  const getActionButton = useCallback(
    (provider: ThirdPartyProvider) => {
      if (provider.status === "connected") {
        return (
          <View className="flex-row items-center gap-2">
            <TouchableOpacity
              onPress={() => handleManageLocations(provider)}
              disabled={connecting === provider.id}
              className="flex-row items-center justify-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 sm:py-2 min-h-[44px] flex-1"
              activeOpacity={0.8}
            >
              {connecting === provider.id ? (
                <>
                  <View className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  <Text className="text-gray-500 font-medium text-sm">
                    Loading...
                  </Text>
                </>
              ) : (
                <>
                  <Text className="text-gray-700 font-medium text-sm">
                    {provider.id === "stream" ? "Manage" : "Manage Locations"}
                  </Text>
                  {provider.id === "stream" ? (
                    <ExternalLink size={16} color="#374151" />
                  ) : (
                    <Store size={16} color="#374151" />
                  )}
                </>
              )}
            </TouchableOpacity>
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                className={`h-11 w-11 sm:h-8 sm:w-8 flex items-center justify-center ${provider.id === "spoton" ? "visible" : "max-sm:hidden sm:invisible"} hidden sm:flex`}
              >
                <Pressable className="h-11 w-11 sm:h-8 sm:w-8 flex items-center justify-center flex-shrink-0">
                  <MoreVertical className="w-4 h-4" />
                </Pressable>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => handleDisconnect(provider.id)}
                >
                  <Unlink className="w-4 h-4 mr-0.5 text-destructive" />
                  Disconnect
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </View>
        );
      }

      return (
        <TouchableOpacity
          onPress={() => handleConnect(provider.id)}
          disabled={connecting === provider.id}
          className="flex-row items-center justify-center gap-2 bg-blue-500 border border-blue-500 rounded-lg px-4 py-3 sm:py-2 min-h-[44px] w-full sm:w-auto mr-10"
          activeOpacity={0.8}
        >
          {connecting === provider.id ? (
            <>
              <View className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              <Text className="text-white font-medium text-sm">
                Connecting...
              </Text>
            </>
          ) : (
            <>
              <Text className="text-white font-medium text-sm">Connect</Text>
              <LogIn size={16} color="white" />
            </>
          )}
        </TouchableOpacity>
      );
    },
    [connecting, handleConnect, handleManageLocations, handleDisconnect]
  );

  return (
    <View className="space-y-6 px-4 sm:px-0">
      <View className="space-y-2 sm:space-y-3">
        <StyledText className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
          Integrations
        </StyledText>
        <StyledText className="text-gray-600 text-sm sm:text-base leading-5 sm:leading-6">
          Connect your third party accounts
        </StyledText>
      </View>

      {isAgentsLoading || loading ? (
        <LoadingView />
      ) : (
        <View className="space-y-6">
          {renderProvidersList(
            availableProviders,
            getActionButton,
            handleDisconnect
          )}
        </View>
      )}

      <SpotOnLocationsModal
        visible={showSpotOnModal}
        onClose={() => setShowSpotOnModal(false)}
      />

      <IntegrationDisconnectConfirmModal
        visible={showDisconnectModal}
        providerName={
          providerToDisconnect === "spoton" ? "SpotOn" : "Integration"
        }
        onConfirm={confirmDisconnect}
        onCancel={cancelDisconnect}
        isLoading={disconnecting === providerToDisconnect || isOAuthFlow}
      />
    </View>
  );
};

export default Integrations;
